import { ChevronDown, ChevronUp, Trash2 } from 'lucide-react';
import { Button, Input, Select, Badge, cn } from '../../ui';
import {
  FIELD_TYPES,
  isNumericFieldType,
  coerceDefaultValue,
  defaultValueToString,
} from '../../features/templates/templateModel';
import type { FieldDef, FieldType } from '../../features/templates/types';

export interface FieldRowProps {
  field: FieldDef;
  index: number;
  total: number;
  onChange: (patch: Partial<FieldDef>) => void;
  onMove: (delta: number) => void;
  onRemove: () => void;
}

const TYPE_OPTIONS = FIELD_TYPES.map((t) => ({ value: t.value, label: t.label }));

/** Inline editor for one configurable template field of any type. */
export function FieldRow({
  field,
  index,
  total,
  onChange,
  onMove,
  onRemove,
}: FieldRowProps) {
  const numeric = isNumericFieldType(field.type);
  const isEnum = field.type === 'enum';

  return (
    <div className="rounded-xl border border-surface-border bg-surface-1 p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Badge tone="neutral">#{index + 1}</Badge>
          <span className="font-mono text-xs text-zinc-500">
            {field.key || '(no key)'}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            variant="ghost"
            aria-label="Move field up"
            disabled={index === 0}
            onClick={() => onMove(-1)}
          >
            <ChevronUp className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            aria-label="Move field down"
            disabled={index === total - 1}
            onClick={() => onMove(1)}
          >
            <ChevronDown className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            aria-label="Remove field"
            className="text-red-400 hover:text-red-300"
            onClick={onRemove}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Input
          label="Label"
          value={field.label}
          placeholder="Backend services"
          onChange={(e) => onChange({ label: e.target.value })}
        />
        <Input
          label="Key"
          value={field.key}
          placeholder="backendServices"
          className="font-mono"
          onChange={(e) => onChange({ key: e.target.value })}
        />
        <Select
          label="Type"
          options={TYPE_OPTIONS}
          value={field.type}
          onChange={(e) => {
            const type = e.target.value as FieldType;
            onChange({
              type,
              ...(type === 'enum' && !field.options ? { options: [] } : {}),
            });
          }}
        />
        <Input
          label="Default value"
          value={defaultValueToString(field.defaultValue)}
          placeholder={field.type === 'boolean' ? 'true / false' : 'optional'}
          onChange={(e) =>
            onChange({ defaultValue: coerceDefaultValue(field.type, e.target.value) })
          }
        />
        <Input
          label="Group"
          value={field.group ?? ''}
          placeholder="Section (optional)"
          onChange={(e) => onChange({ group: e.target.value || undefined })}
        />
        <Input
          label="Help text"
          value={field.helpText ?? ''}
          placeholder="Guidance (optional)"
          onChange={(e) => onChange({ helpText: e.target.value || undefined })}
        />

        {numeric && (
          <>
            <Input
              label="Min"
              type="number"
              value={field.min ?? ''}
              placeholder="none"
              onChange={(e) =>
                onChange({
                  min: e.target.value === '' ? undefined : Number(e.target.value),
                })
              }
            />
            <Input
              label="Max"
              type="number"
              value={field.max ?? ''}
              placeholder="none"
              onChange={(e) =>
                onChange({
                  max: e.target.value === '' ? undefined : Number(e.target.value),
                })
              }
            />
          </>
        )}

        {isEnum && (
          <Input
            label="Options (comma-separated)"
            className={cn('sm:col-span-2 lg:col-span-3')}
            value={(field.options ?? []).join(', ')}
            placeholder="Low, Medium, High"
            onChange={(e) =>
              onChange({
                options: e.target.value
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean),
              })
            }
          />
        )}
      </div>
    </div>
  );
}
